import { InCallChatPanel } from './in-call-chat-panel'
import { ParticipantRosterPanel } from './participant-roster-panel'
import type { CallParticipant } from '../../../types/voice-call'
import type { UseInCallChatResult } from '../hooks/use-in-call-chat'

export type GroupCallSidebarTab = 'chat' | 'screen' | 'people'

type GroupCallSidebarProps = {
  activeTab: GroupCallSidebarTab
  onTabChange: (tab: GroupCallSidebarTab) => void
  onClose: () => void

  // Chat (call-scoped — owned by the overlay so the live subscription survives
  // opening/closing the panel)
  chat: UseInCallChatResult
  currentUserId: string | undefined
  resolveName: (senderId: string) => string

  // Screen share
  bindContentVideo: (el: HTMLVideoElement | null) => void
  isPresenting: boolean
  remotePresenting: boolean
  presenterName: string | null
  canScreenShare: boolean
  onToggleShare: () => void

  // People
  participants: CallParticipant[]
  isHost: boolean
  atCap: boolean
  liveCount: number
  maxParticipants: number
  selfMuted: boolean
  onAddPeople: () => void
  onAskToMute: (userId: string) => void
  onRemove: (userId: string) => void
  onReAdd: (participant: CallParticipant) => void
}

function TabButton({
  label,
  active,
  onClick,
  badge,
}: {
  label: string
  active: boolean
  onClick: () => void
  badge?: number
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition ${
        active ? 'bg-white/15 text-white' : 'text-white/60 hover:text-white'
      }`}
    >
      {label}
      {typeof badge === 'number' && badge > 0 && (
        <span className="ml-1 rounded-full bg-white/20 px-1.5 text-[10px]">{badge}</span>
      )}
    </button>
  )
}

export function GroupCallSidebar({
  activeTab,
  onTabChange,
  onClose,
  chat,
  currentUserId,
  resolveName,
  bindContentVideo,
  isPresenting,
  remotePresenting,
  presenterName,
  canScreenShare,
  onToggleShare,
  participants,
  isHost,
  atCap,
  liveCount,
  maxParticipants,
  selfMuted,
  onAddPeople,
  onAskToMute,
  onRemove,
  onReAdd,
}: GroupCallSidebarProps) {
  const presenting = isPresenting || remotePresenting

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-900/95 text-white">
      <div className="flex shrink-0 items-center gap-1 border-b border-white/10 p-2">
        <div className="flex flex-1 gap-1" role="tablist" aria-label="Call panel">
          <TabButton label="Chat" active={activeTab === 'chat'} onClick={() => onTabChange('chat')} />
          <TabButton
            label="Screen"
            active={activeTab === 'screen'}
            onClick={() => onTabChange('screen')}
            badge={presenting ? 1 : undefined}
          />
          <TabButton
            label="People"
            active={activeTab === 'people'}
            onClick={() => onTabChange('people')}
            badge={liveCount}
          />
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="rounded-lg px-2 py-1 text-white/60 hover:bg-white/10 hover:text-white"
        >
          ✕
        </button>
      </div>

      <div className="min-h-0 flex-1">
        <div className={activeTab === 'chat' ? 'h-full' : 'hidden'} role="tabpanel" aria-label="Chat">
          <InCallChatPanel chat={chat} resolveName={resolveName} />
        </div>

        {/* Screen tab is kept mounted so the content <video> always has a bind
            target while presenting, even when another tab is shown. */}
        <div className={activeTab === 'screen' ? 'flex h-full flex-col' : 'hidden'} role="tabpanel" aria-label="Screen share">
          <div className="relative flex-1 bg-black">
            <video
              ref={bindContentVideo}
              className={`h-full w-full object-contain ${presenting ? 'opacity-100' : 'opacity-0'}`}
              autoPlay
              playsInline
              muted
              aria-label={presenterName ? `${presenterName} is presenting` : 'Shared screen'}
            />
            {!presenting && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-sm text-white/50">
                <span>No one is sharing their screen.</span>
                {canScreenShare && (
                  <button
                    type="button"
                    onClick={onToggleShare}
                    className="rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20"
                  >
                    Share your screen
                  </button>
                )}
              </div>
            )}
            {presenting && (
              <span className="absolute left-2 top-2 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-white">
                {isPresenting ? 'You are sharing' : `${presenterName ?? 'Someone'} is presenting`}
              </span>
            )}
          </div>
          {canScreenShare && presenting && (
            <div className="shrink-0 border-t border-white/10 p-2">
              <button
                type="button"
                onClick={onToggleShare}
                className={`w-full rounded-lg px-3 py-2 text-xs font-semibold transition ${
                  isPresenting
                    ? 'bg-red-500/80 text-white hover:bg-red-600'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {isPresenting ? 'Stop sharing' : 'Share your screen'}
              </button>
            </div>
          )}
        </div>

        <div className={activeTab === 'people' ? 'h-full' : 'hidden'} role="tabpanel" aria-label="People">
          <ParticipantRosterPanel
            participants={participants}
            currentUserId={currentUserId}
            isHost={isHost}
            atCap={atCap}
            liveCount={liveCount}
            maxParticipants={maxParticipants}
            selfMuted={selfMuted}
            onAddPeople={onAddPeople}
            onAskToMute={onAskToMute}
            onRemove={onRemove}
            onReAdd={onReAdd}
          />
        </div>
      </div>
    </div>
  )
}
